import { Box, Typography, styled } from '@mui/material'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { ConfigureView, QuickSelectMode, ZoomDirection } from 'src/generated-types/configure-positions'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { ConfigurePalette } from './configurePalette'

const VIEWS: ConfigureView[] = ['drawing', 'list']
const QUICK_SELECT_MODES: QuickSelectMode[] = ['unconfigured', 'all', 'none']
export const TOOLBAR_HEIGHT = '32px'

interface DrawingToolbarProps {
  view: ConfigureView
  zoom: number
  palette: ConfigurePalette
  onView: (view: ConfigureView) => void
  onQuickSelect: (mode: QuickSelectMode) => void
  onZoom: (direction: ZoomDirection) => void
  // Only set while a revision is open.
  onSelectChanged?: () => void
}

const DrawingToolbar: React.FC<DrawingToolbarProps> = ({
  view,
  zoom,
  palette,
  onView,
  onQuickSelect,
  onZoom,
  onSelectChanged,
}) => {
  const { t } = useTranslation(['position', 'common', 'projects'])

  const viewLabel = (item: ConfigureView) =>
    item === 'drawing' ? t('drawing', { ns: 'position' }) : t('list', { ns: 'common' })
  const quickSelectLabel = (mode: QuickSelectMode) => {
    if (mode === 'unconfigured') return t('unconfigured', { ns: 'projects' })
    return mode === 'all' ? t('all', { ns: 'common' }) : t('clear', { ns: 'common' })
  }

  return (
    <ToolbarRow>
      <SegmentGroup>
        {VIEWS.map(item => {
          const isActive = view === item
          return (
            <GridActionButton
              key={item}
              aria-pressed={isActive}
              onClick={() => onView(item)}
              buttonText={viewLabel(item)}
              bgColor={isActive ? palette.primary : undefined}
              textColor={isActive ? palette.paper : palette.primary}
              buttonTextProps={{ fontSize: 13 }}
              height={TOOLBAR_HEIGHT}
              border={0}
              shadowWeight={null}
              sx={segmentSx}
            />
          )
        })}
      </SegmentGroup>
      <SegmentGroup sx={{ ml: 'auto' }}>
        {QUICK_SELECT_MODES.map(mode => (
          <React.Fragment key={mode}>
            {mode === 'none' && onSelectChanged && (
              <GridActionButton
                onClick={onSelectChanged}
                buttonText={t('revision-changed', { ns: 'projects' })}
                textColor={palette.warningText}
                buttonTextProps={{ fontSize: 12, fontWeight: 500 }}
                height={TOOLBAR_HEIGHT}
                border={0}
                shadowWeight={null}
                sx={segmentSx}
              />
            )}
            <GridActionButton
              onClick={() => onQuickSelect(mode)}
              buttonText={quickSelectLabel(mode)}
              textColor={mode === 'none' ? palette.muted : palette.primary}
              buttonTextProps={{ fontSize: 12, fontWeight: 500 }}
              height={TOOLBAR_HEIGHT}
              border={0}
              shadowWeight={null}
              sx={segmentSx}
            />
          </React.Fragment>
        ))}
      </SegmentGroup>
      {view === 'drawing' && (
        <SegmentGroup>
          <GridActionButton
            aria-label={t('zoom-out', { ns: 'common' })}
            toolTipTitle={t('zoom-out', { ns: 'common' })}
            onClick={() => onZoom('out')}
            buttonText="−"
            buttonTextProps={{ fontSize: 13 }}
            height={TOOLBAR_HEIGHT}
            width={TOOLBAR_HEIGHT}
            border={0}
            shadowWeight={null}
            sx={segmentSx}
          />
          <ZoomLevel>{Math.round(zoom * 100)}%</ZoomLevel>
          <GridActionButton
            aria-label={t('zoom-in', { ns: 'common' })}
            toolTipTitle={t('zoom-in', { ns: 'common' })}
            onClick={() => onZoom('in')}
            buttonText="+"
            buttonTextProps={{ fontSize: 13 }}
            height={TOOLBAR_HEIGHT}
            width={TOOLBAR_HEIGHT}
            border={0}
            shadowWeight={null}
            sx={segmentSx}
          />
          <GridActionButton
            onClick={() => onZoom('fit')}
            buttonText={t('fit-view', { ns: 'position' })}
            buttonTextProps={{ fontSize: 13 }}
            height={TOOLBAR_HEIGHT}
            border={0}
            shadowWeight={null}
            sx={segmentSx}
          />
        </SegmentGroup>
      )}
    </ToolbarRow>
  )
}

export default DrawingToolbar

export const segmentSx = { borderRadius: 0, px: 1.25 }

export const ToolbarRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
}))

/** Joins its buttons into one bordered, divided control. */
export const SegmentGroup = styled(Box)(({ theme }) => {
  const line = getThemeColor(theme, MUIThemeColors.secondaryLight)
  return {
    display: 'inline-flex',
    alignItems: 'stretch',
    height: TOOLBAR_HEIGHT,
    border: `1px solid ${line}`,
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: getThemeColor(theme, MUIThemeColors.white),
    '& > *:not(:first-of-type)': { borderLeft: `1px solid ${line}` },
  }
})

export const ZoomLevel = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 44,
  padding: theme.spacing(0, 0.5),
  fontSize: 12,
  fontWeight: 600,
  color: getThemeColor(theme, MUIThemeColors.secondaryDark),
}))
